import {
  FunctionalUnitDTO,
  FunctionalUnitFilters,
  FunctionalUnitService,
  CreateFunctionalUnitDTO,
  UpdateFunctionalUnitDTO,
} from '../../services/functionalUnitService'
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { PaginatedDto } from '@/services/extendedType'

interface FunctionalUnitState {
  list: FunctionalUnitDTO[]
  filters: FunctionalUnitFilters
  loading: boolean
  error: string | null
}

const initialState: FunctionalUnitState = {
  list: [],
  filters: {
    search: '',
  },
  loading: false,
  error: null,
}

export const fetchFunctionalUnits = createAsyncThunk<
  PaginatedDto<FunctionalUnitDTO>,
  FunctionalUnitFilters
>('functionalUnit/fetchAll', async (filters, { rejectWithValue }) => {
  try {
    return await FunctionalUnitService.getAll(filters)
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message ||
        error.message ||
        'Failed to fetch functional units',
    )
  }
})

export const fetchFunctionalUnit = createAsyncThunk<FunctionalUnitDTO, number>(
  'functionalUnit/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      return await FunctionalUnitService.findOne(id)
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          'Failed to fetch functional unit',
      )
    }
  },
)

export const createFunctionalUnit = createAsyncThunk<
  FunctionalUnitDTO,
  CreateFunctionalUnitDTO
>('functionalUnit/create', async (data, { rejectWithValue }) => {
  try {
    return await FunctionalUnitService.create(data)
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message ||
        error.message ||
        'Failed to create functional unit',
    )
  }
})

export const updateFunctionalUnit = createAsyncThunk<
  FunctionalUnitDTO,
  { id: number; data: UpdateFunctionalUnitDTO }
>('functionalUnit/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await FunctionalUnitService.update(id, data)
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message ||
        error.message ||
        'Failed to update functional unit',
    )
  }
})

export const deleteFunctionalUnit = createAsyncThunk<number, number>(
  'functionalUnit/delete',
  async (id, { rejectWithValue }) => {
    try {
      await FunctionalUnitService.delete(id)
      return id
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          'Failed to delete functional unit',
      )
    }
  },
)

const functionalUnitSlice = createSlice({
  name: 'functionalUnit',
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<FunctionalUnitFilters>>,
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFunctionalUnits.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFunctionalUnits.fulfilled, (state, action) => {
        state.loading = false
        state.list = action.payload.datas
      })
      .addCase(fetchFunctionalUnits.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as string) || 'Failed to fetch functional units'
      })
      .addCase(createFunctionalUnit.fulfilled, (state, action) => {
        state.list.push(action.payload)
        state.error = null
      })
      .addCase(createFunctionalUnit.rejected, (state, action) => {
        state.error =
          (action.payload as string) || 'Failed to create functional unit'
      })
      .addCase(updateFunctionalUnit.fulfilled, (state, action) => {
        const index = state.list.findIndex(
          (item) => item.id === action.payload.id,
        )
        if (index !== -1) {
          state.list[index] = action.payload
        }
        state.error = null
      })
      .addCase(updateFunctionalUnit.rejected, (state, action) => {
        state.error =
          (action.payload as string) || 'Failed to update functional unit'
      })
      .addCase(deleteFunctionalUnit.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteFunctionalUnit.fulfilled, (state, action) => {
        state.loading = false
        state.list = state.list.filter((item) => item.id !== action.payload)
      })
      .addCase(deleteFunctionalUnit.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as string) || 'Failed to delete functional unit'
      })
  },
})

export const { setFilters, clearError } = functionalUnitSlice.actions
export default functionalUnitSlice.reducer
